'use client'

import {
  IconDocumentPaperGray,
  IconThumbsUpFill,
} from '@macarongfactory/web-icons'
import { Divider, Label, Text } from '@macarongfactory/web-ui'

import SectionHeading from './SectionHeading'
import {
  ANNUAL_DISTANCE_KM,
  MODOOCAR_CODE,
  calcAnnualFuelCost,
  useModoocarImpression,
  useModoocarTracking,
} from '../_features/modoocar'

import type { IModoocarDetail } from '../_features/modoocar'

interface IFuelCostSectionProps {
  detail: IModoocarDetail
}

/** 라벨 아이콘 크기(px) — 실연비 섹션과 동일 규격 */
const LABEL_ICON_SIZE_PX = 14

/** 유류비 순위 라벨 강조 임계 — 이내면 브랜드색+👍, 초과면 회색 (Figma +유류비 케이스) */
const TOP_RANK_LABEL_THRESHOLD = 10
/** 데이터 없음 빈 상태 document 아이콘 크기(px) */
const EMPTY_ICON_SIZE_PX = 56

/** 연간/월 유류비 강조 카드 — 제목 + 금액(만원) + 보조 캡션 */
const FuelCostCard = ({
  title,
  manWon,
  caption,
}: {
  title: string
  manWon: number
  caption: string
}) => (
  <div className='flex flex-1 flex-col items-center gap-dimension-x2-5 rounded-dimension-x4 border border-stroke-neutral-strong bg-background-surface-default px-dimension-x4 py-dimension-x5 shadow-weak'>
    <Text variant='title-2-semibold' color='text-foreground-neutral-strong'>
      {title}
    </Text>
    <div className='flex items-end gap-dimension-x0-5'>
      <Text variant='display-1-semibold' color='text-foreground-brand-strong'>
        {manWon.toLocaleString()}
      </Text>
      <Text
        variant='title-2-semibold'
        color='text-foreground-brand-strong'
        className='pb-dimension-x1'
      >
        만원
      </Text>
    </div>
    <Text variant='body-1-medium' color='text-foreground-neutral-assistive'>
      {caption}
    </Text>
  </div>
)

/** 비용 항목 한 줄 */
const CostRow = ({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) => (
  <div className='flex items-center justify-between'>
    <Text variant='title-2-medium' color='text-foreground-neutral-strong'>
      {label}
    </Text>
    <div className='flex flex-col items-end gap-dimension-x0-5'>
      <Text variant='title-1-semibold' color='text-foreground-neutral-strong'>
        {value}
      </Text>
      {sub ? (
        <Text
          variant='caption-1-medium'
          color='text-foreground-neutral-alternative'
        >
          {sub}
        </Text>
      ) : null}
    </div>
  </div>
)

/**
 * 유류비 섹션 — 연간/월 유류비 (신규). 분기 고정 유가 기반 계산.
 */
const FuelCostSection = ({ detail }: IFuelCostSectionProps) => {
  const { upperModelName, realFuel, fuelCost, fuelCostPercentile, fuelType } =
    detail

  // 전기차는 '유류비/유가/원-L' 대신 '전기 요금/전기 요금/원-kWh'로 표기 (Figma 디자인 가이드)
  const isElectric = fuelType === '전기'
  const costLabel = isElectric ? '전기 요금' : '유류비'
  const priceRowLabel = isElectric ? '기준 전기 요금' : '기준 유가'
  const priceUnit = isElectric ? '원/kWh' : '원/L'

  const { sectionShow } = useModoocarTracking()
  const sectionRef = useModoocarImpression<HTMLElement>(() =>
    sectionShow(MODOOCAR_CODE.FUEL_COST, '유류비')
  )

  // 유류비 데이터(실연비·유가) 없으면 빈 상태 노출 (Figma 8063:16434 — data=no)
  const hasFuelCostData =
    realFuel.realFuelEconomy > 0 && fuelCost.fuelPricePerLiter > 0
  if (!hasFuelCostData) {
    return (
      <section
        id='fuel-cost'
        className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 px-dimension-x5 py-dimension-x8 lg:scroll-mt-[112px]'
      >
        <h2 className='flex min-w-0 items-center'>
          <Text
            as='span'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
            className='min-w-0 truncate'
          >
            {upperModelName}
          </Text>
          <Text
            as='span'
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
            className='shrink-0 whitespace-nowrap'
          >
            &nbsp;{costLabel}
          </Text>
        </h2>
        <div className='flex flex-col items-center gap-dimension-x4 py-dimension-x10'>
          <IconDocumentPaperGray
            width={EMPTY_ICON_SIZE_PX}
            height={EMPTY_ICON_SIZE_PX}
          />
          <Text
            variant='title-1-semibold'
            color='text-foreground-neutral-strong'
          >
            아직 {costLabel} 정보가 없는 차량이에요
          </Text>
        </div>
      </section>
    )
  }

  const cost = calcAnnualFuelCost({
    realFuelEconomy: realFuel.realFuelEconomy,
    fuelPricePerLiter: fuelCost.fuelPricePerLiter,
    annualWon: fuelCost.annualFuelCostWon,
    monthlyWon: fuelCost.monthlyFuelCostWon,
    perKmCost: fuelCost.costPerKm,
  })

  return (
    <section
      ref={sectionRef}
      id='fuel-cost'
      className='flex scroll-mt-[var(--mc-section-scroll-mt,97px)] flex-col gap-dimension-x4 px-dimension-x5 py-dimension-x8 lg:scroll-mt-[112px]'
    >
      <SectionHeading
        title={
          <>
            {/* 1줄 초과 시 모델명만 말줄임, '유류비'는 항상 노출 (Figma 어노테이션) */}
            <span className='min-w-0 truncate'>{upperModelName}</span>
            <span className='shrink-0'>{`\u00A0${costLabel}`}</span>
          </>
        }
        titleVariant='title-1-semibold'
        labelEnd
        label={
          fuelCostPercentile > 0 ? (
            // 상위권(≤10%)은 브랜드색 + 👍, 그 외는 회색·아이콘 없음 (Figma +유류비 케이스)
            <Label
              text={`상위 ${fuelCostPercentile}%`}
              type='weak'
              variant={
                fuelCostPercentile <= TOP_RANK_LABEL_THRESHOLD
                  ? 'brand'
                  : 'neutral'
              }
              size='small'
              leadingIcon={
                fuelCostPercentile <= TOP_RANK_LABEL_THRESHOLD ? (
                  <IconThumbsUpFill
                    width={LABEL_ICON_SIZE_PX}
                    height={LABEL_ICON_SIZE_PX}
                  />
                ) : undefined
              }
            />
          ) : undefined
        }
      />

      {/* 연간 / 월 유류비 카드 */}
      <div className='flex gap-dimension-x2-5'>
        <FuelCostCard
          title={`연간 ${costLabel}`}
          manWon={cost.annualManWon}
          caption={`연 ${ANNUAL_DISTANCE_KM.toLocaleString()}km 기준`}
        />
        <FuelCostCard
          title={`월 ${costLabel}`}
          manWon={cost.monthlyManWon}
          caption={`연간 ${costLabel} ÷ 12`}
        />
      </div>

      {/* 계산 기준 — 행 사이에만 구분선 (Figma: list/Divider/list/Divider/list) */}
      <div className='flex flex-col gap-dimension-x4 pt-dimension-x4'>
        <CostRow
          label='기준 주행거리'
          value={`연 ${ANNUAL_DISTANCE_KM.toLocaleString()}km`}
        />
        <Divider.Horizontal />
        <CostRow
          label={priceRowLabel}
          value={`${fuelCost.fuelPricePerLiter.toLocaleString()}${priceUnit}`}
          sub={detail.dataBasisDate ?? fuelCost.fuelPriceYearMonth}
        />
        <Divider.Horizontal />
        <CostRow
          label='1km 당 주행비용'
          value={`${cost.costPerKm.toLocaleString()}원`}
        />
      </div>
    </section>
  )
}

export default FuelCostSection
